import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import {
  genderOptions,
  militaryServiceOptions,
  requestedResumeLanguageOptions,
  photoIncludedOptions,
} from "./mockData";
import { Country, City } from "../../../../types/site-params/Country";
import { Autocomplete, CircularProgress, FormHelperText } from "@mui/material";
import { CompanyActivity } from "../../../../types/companies-managment/companyActivity";
import { JobType } from "../../../../types/site-params/jobType";
import { Language } from "../../../../types/site-params/language";
import { useCountriesForAutocomplete } from "../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../hooks/query-hooks/useCities";
import { useJobActivitiesForAutocomplete } from "../../../../hooks/query-hooks/useJobActivities";
import { useJobTypesForAutocomplete } from "../../../../hooks/query-hooks/useJobTypes";
import { useLanguagesForAutocomplete } from "../../../../hooks/query-hooks/useLanguages";
import { useDebounce } from "../../../../hooks";

export type ResumeFiltersType = {
  requested_resume_language: string;
  desired_work_department: string;
  country_id: string;
  city_id: string;
  targeted_job_type: string;
  foreign_languages: string;
  gender: string;
  military_service: string;
  photo_included: string;
  status: string;
};

export type FiltersModalProps = {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: ResumeFiltersType) => void;
  onClearFilters: () => void;
};

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
}: FiltersModalProps) => {
  const theme = useTheme();

  const [requestedResumeLanguage, setRequestedResumeLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [militaryService, setMilitaryService] = useState("");
  const [photoIncluded, setPhotoIncluded] = useState("");
  const [status, setStatus] = useState("");

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedJobActivity, setSelectedJobActivity] =
    useState<CompanyActivity | null>(null);
  const [selectedJobType, setSelectedJobType] = useState<JobType | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [jobActivitySearchTerm, setJobActivitySearchTerm] = useState("");
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
  const [languageSearchTerm, setLanguageSearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedJobActivitySearchTerm = useDebounce(
    jobActivitySearchTerm,
    500
  );
  const debouncedJobTypeSearchTerm = useDebounce(jobTypeSearchTerm, 500);
  const debouncedLanguageSearchTerm = useDebounce(languageSearchTerm, 500);

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    selectedCountry?.id || null,
    20,
    debouncedCitySearchTerm
  );

  const {
    items: jobActivities,
    loading: loadingJobActivities,
    hasMore: hasMoreJobActivities,
    loadMore: loadMoreJobActivities,
    error: jobActivitiesError,
  } = useJobActivitiesForAutocomplete(20, debouncedJobActivitySearchTerm);

  const {
    items: jobTypes,
    loading: loadingJobTypes,
    hasMore: hasMoreJobTypes,
    loadMore: loadMoreJobTypes,
    error: jobTypesError,
  } = useJobTypesForAutocomplete(20, debouncedJobTypeSearchTerm);

  const {
    items: languages,
    loading: loadingLanguages,
    hasMore: hasMoreLanguages,
    loadMore: loadMoreLanguages,
    error: languagesError,
  } = useLanguagesForAutocomplete(20, debouncedLanguageSearchTerm);

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  const handleApply = () => {
    onApplyFilters({
      requested_resume_language: requestedResumeLanguage,
      gender,
      military_service: militaryService,
      photo_included: photoIncluded,
      targeted_job_type: selectedJobType ? selectedJobType.id.toString() : "",
      desired_work_department: selectedJobActivity
        ? selectedJobActivity.id.toString()
        : "",
      country_id: selectedCountry ? selectedCountry.id.toString() : "",
      city_id: selectedCity ? selectedCity.id.toString() : "",
      foreign_languages: selectedLanguage ? selectedLanguage.id.toString() : "",
      status,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
        <FormattedMessage id="Filters" />
      </DialogTitle>
      <Divider sx={{ marginInline: "1rem" }} />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} marginTop=".5rem">
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCountry}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue);
                  setSelectedCity(null);
                  // Clear search term when option is selected
                  if (newValue) {
                    setCountrySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCountrySearchTerm(newInputValue);
                  }
                }}
                options={countries}
                getOptionLabel={(option: Country) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Desired Work Location" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCountries && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCountries &&
                      !loadingCountries
                    ) {
                      loadMoreCountries();
                    }
                  },
                }}
                loading={loadingCountries}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCountrySearchTerm.length >= 2
                    ? "No countries found"
                    : "Type to search countries"
                }
              />
              {countriesError && (
                <FormHelperText error>
                  Error loading countries. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCity}
                onChange={(event, newValue) => {
                  setSelectedCity(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setCitySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCitySearchTerm(newInputValue);
                  }
                }}
                options={cities}
                getOptionLabel={(option: City) => option.name}
                disabled={!selectedCountry}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="City" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCities && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCities &&
                      !loadingCities
                    ) {
                      loadMoreCities();
                    }
                  },
                }}
                loading={loadingCities}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCitySearchTerm.length >= 2
                    ? "No cities found"
                    : "Type to search cities"
                }
              />
              {citiesError && (
                <FormHelperText error>
                  Error loading cities. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="resume-language-label">
                  Requested Resume Language
                </InputLabel>
                <Select
                  labelId="resume-language-label"
                  value={requestedResumeLanguage}
                  label="Requested Resume Language"
                  onChange={(e) => setRequestedResumeLanguage(e.target.value)}
                >
                  {requestedResumeLanguageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  value={gender}
                  label="Gender"
                  onChange={(e) => setGender(e.target.value)}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="military-service-label">
                  Military Service
                </InputLabel>
                <Select
                  labelId="military-service-label"
                  value={militaryService}
                  label="Military Service"
                  onChange={(e) => setMilitaryService(e.target.value)}
                >
                  {militaryServiceOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="photo-included-label">
                  Photo Included
                </InputLabel>
                <Select
                  labelId="photo-included-label"
                  value={photoIncluded}
                  label="Photo Included"
                  onChange={(e) => setPhotoIncluded(e.target.value)}
                >
                  {photoIncludedOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Unactive">Unactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedJobActivity}
                onChange={(event, newValue) => {
                  setSelectedJobActivity(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setJobActivitySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setJobActivitySearchTerm(newInputValue);
                  }
                }}
                options={jobActivities}
                getOptionLabel={(option: CompanyActivity) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Desired Work Department" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingJobActivities && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreJobActivities &&
                      !loadingJobActivities
                    ) {
                      loadMoreJobActivities();
                    }
                  },
                }}
                loading={loadingJobActivities}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedJobActivitySearchTerm.length >= 2
                    ? "No job activities found"
                    : "Type to search job activities"
                }
              />
              {jobActivitiesError && (
                <FormHelperText error>
                  Error loading job activities. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedJobType}
                onChange={(event, newValue) => {
                  setSelectedJobType(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setJobTypeSearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setJobTypeSearchTerm(newInputValue);
                  }
                }}
                options={jobTypes}
                getOptionLabel={(option: JobType) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Targeted Job Type" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingJobTypes && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreJobTypes &&
                      !loadingJobTypes
                    ) {
                      loadMoreJobTypes();
                    }
                  },
                }}
                loading={loadingJobTypes}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedJobTypeSearchTerm.length >= 2
                    ? "No job types found"
                    : "Type to search job types"
                }
              />
              {jobTypesError && (
                <FormHelperText error>
                  Error loading job types. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedLanguage}
                onChange={(event, newValue) => {
                  setSelectedLanguage(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setLanguageSearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setLanguageSearchTerm(newInputValue);
                  }
                }}
                options={languages}
                getOptionLabel={(option: Language) =>
                  theme.direction === "rtl" ? option.name_trans.ar : option.name
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Foreign Languages" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingLanguages && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreLanguages &&
                      !loadingLanguages
                    ) {
                      loadMoreLanguages();
                    }
                  },
                }}
                loading={loadingLanguages}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedLanguageSearchTerm.length >= 2
                    ? "No languages found"
                    : "Type to search languages"
                }
              />
              {languagesError && (
                <FormHelperText error>
                  Error loading languages. Please try again.
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Apply" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
